import { AddIcon } from "@minket/brand/icons/add";
import { SendIcon } from "@minket/brand/icons/send";
import { Button } from "@minket/ui/components/button";
import { useQuery } from "@tanstack/react-query";
import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { useMarkStepDone } from "@/components/onboarding/mark-done";
import { StepPage } from "@/components/onboarding/step-page";
import { UnavailableAction } from "@/components/page/action";
import { Notice } from "@/components/page/states";
import { useWebhookDialogs } from "@/components/webhooks/host";
import { deliveriesQuery } from "@/components/webhooks/query";
import { describeError } from "@/lib/api";
import { endpoints } from "@/lib/endpoints";
import { useCan } from "@/lib/permissions";
import { apiQuery } from "@/lib/query";
import { useWorkspace } from "@/lib/workspace";

export const Route = createFileRoute("/onboarding/webhooks")({
  component: WebhooksStep,
});

const addEndpoint = { label: "Add endpoint", icon: AddIcon };

function WebhooksStep() {
  return (
    <StepPage
      path="/onboarding/webhooks"
      intro="Add an https endpoint for this project's events, then send it a test event and see what it answers."
      asks={[
        {
          title: "An https URL",
          text: "Public and without credentials in it; private and internal addresses are refused.",
        },
        {
          title: "Its events",
          text: "Which events it receives: sent, delivered, bounced, complained, opened, clicked and more.",
        },
      ]}
      action={addEndpoint}
      control={<StepControl />}
    />
  );
}

/** Add the first endpoint, then send it a test event and show what it answered. */
function StepControl() {
  const { project } = useWorkspace();
  const canManage = useCan("webhooks.manage");
  const projectId = project?.id ?? "";
  const list = useQuery({ ...apiQuery(endpoints.webhooks, { projectId }), enabled: !!project });
  const dialogs = useWebhookDialogs();
  const [testId, setTestId] = useState<string | null>(null);

  const unavailable = (reason: string) => (
    <UnavailableAction {...addEndpoint} reason={reason} align="start" />
  );
  if (!project) return unavailable("Choose a project first");
  if (list.isPending) return unavailable("Loading endpoints");
  if (list.isError) {
    return unavailable(describeError(list.error).message);
  }
  if (!canManage) {
    return unavailable("Viewers cannot add endpoints");
  }
  const newest = list.data.data[0];

  return (
    <div className="grid max-w-md min-w-0 gap-2">
      {newest ? (
        <>
          <Button
            className="w-fit"
            onClick={() =>
              dialogs.open({ kind: "test", projectId, endpoint: newest, onSent: setTestId })
            }
          >
            <SendIcon className="size-5" />
            Send a test event
          </Button>
          <p className="text-sm text-muted-foreground wrap-anywhere">
            To <span className="type-data">{newest.url}</span>
          </p>
        </>
      ) : (
        <Button className="w-fit" onClick={() => dialogs.open({ kind: "create", projectId })}>
          <AddIcon className="size-5" />
          Add endpoint
        </Button>
      )}
      {testId && newest ? (
        <TestResult projectId={projectId} webhookId={newest.id} deliveryId={testId} />
      ) : null}
      {dialogs.element}
    </div>
  );
}

function TestResult({
  projectId,
  webhookId,
  deliveryId,
}: {
  projectId: string;
  webhookId: string;
  deliveryId: string;
}) {
  const query = useQuery(deliveriesQuery(projectId, webhookId));
  const delivery = query.data?.data.find((item) => item.id === deliveryId);
  const markDone = useMarkStepDone("webhooks");
  const delivered = delivery?.status === "succeeded";
  useEffect(() => {
    if (delivered) markDone();
  }, [delivered, markDone]);

  return (
    <div role="status">
      {!delivery || delivery.status === "pending" ? (
        <Notice>Sending the test event.</Notice>
      ) : delivered ? (
        <Notice tone="success">
          Delivered: the endpoint answered{" "}
          <span className="type-data">{delivery.last_status_code}</span>.
        </Notice>
      ) : (
        <Notice tone="destructive">
          Not delivered. {delivery.last_error ?? "The endpoint did not answer."}
        </Notice>
      )}
    </div>
  );
}
